import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import {
  formatCurrency,
  formatDate,
  formatNumber,
} from "../../utils/helper";
import DealStagePopup from "../DealStagePopup";
import { formatText } from "./../../utils/helper";
import { useNavigate } from "react-router-dom";
import ImportantDatesPanel from "./ImportantDatesPanel";

function DealSummary({
  deal,
  updatedealstage,
  isShowStatus,
  isView = false,
  embedded = false,
  editable = false,
  editValues = {},
  onEditChange,
}) {
  const [{ user }, dispatch] = useStateValue();
  const [dealstagepopup, setdealstagepopup] = useState(false);
  // CMA section can be collapsed — same affordance the SellerForm has on
  // its Pre-Listing / Marketing / Contacts sections, so the deal-detail
  // page is consistently collapsible.
  const [cmaOpen, setCmaOpen] = useState(true);
  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate(`/dashboard/deals-detail/${deal.createdat}?page=deal-board`);
  };

  // When embedded inside another card (Buy Side Details), drop our own
  // card chrome — the parent provides it. We also drop duplicate cells:
  // Sale Price + Earnest Money show in Commission Breakdown, and the
  // Deal Stage already lives in the header pill.
  const wrapperClass = embedded
    ? ""
    : "bg-white rounded-xl shadow p-6";
  const headerClass = embedded
    ? "hidden"
    : "flex justify-between items-center border-b border-black/10 pb-2";
  // Lighter cell treatment when embedded: label-on-top + bold value, no
  // gray pill background. Matches the BuyerForm Field component so the
  // merged card reads as one consistent design.
  const cellClass = embedded
    ? ""
    : "p-4 bg-gray-50 rounded-lg";
  // In embedded mode the surrounding form (SellerForm) renders its own
  // fields as bordered, rounded boxes. Wrap the read-only values in the
  // same box treatment (but still bold) so the embedded summary visually
  // matches the inputs below it. Non-embedded keeps the lighter plain
  // text on a gray pill, since the cell itself already provides chrome.
  const valueClass = embedded
    ? "font-semibold text-sm rounded-lg px-2 py-1.5 mt-1 border-2 border-gray-300"
    : "font-semibold";

  return (
    <div className={wrapperClass}>
      <div className={headerClass}>
        <div className={` ${isView ? 'flex justify-between items-center w-full' : ''}`}>
          <h3 className="text-lg font-semibold">Deal Summary</h3>
          {isView && (
            <button
              onClick={() => handleRedirect()}
              className="bg-[#1E293B] text-white px-4 py-2 rounded-lg flex items-center gap-x-1"
            >
              View Detail
            </button>
          )}
        </div>
        {user.role != "Accountant" && isShowStatus && (
          <div className="relative">
            <div
              onClick={() => {
                setdealstagepopup(!dealstagepopup);
              }}
              className=" hover:cursor-pointer  hover:text-white text-black hover:bg-[#1E293B] bg-gray-100 rounded-full px-5 py-1  "
            >
              <p className="font-medium flex items-center ">{deal.dealStage}</p>
            </div>
            <DealStagePopup
              style={{ top: "40px" }}
              visible={dealstagepopup}
              activateoutsidecontainerclick={() => {
                setdealstagepopup(false);
              }}
              onClickcorrect={(item) => {
                updatedealstage(item);
              }}
            />
          </div>
        )}
      </div>

      {/* Important Dates (inspection / financing / closing) only become
          relevant once the deal is Under Contract — the deadlines don't
          exist before there's an accepted offer. Hide for earlier stages
          so the summary stays focused on pre-listing info. */}
      {[
        "Under Contract",
        "Pending",
        "Closed",
        "Cancelled",
        "Expired",
      ].includes(deal?.dealStage) && (
        <ImportantDatesPanel deal={deal} embedded />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
        {/* Split Type / Type / Broker Admin Fee moved to Commission
            Breakdown when embedded — they're commission-context and the
            user wants them gated to the same role-restricted card. */}
        {!embedded && deal.splitdescription && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Split Type</p>
            <p className={valueClass}>{deal.splitdescription}</p>
          </div>
        )}
        {/* Deal Stage lives in the header pill at the top of the card —
            no need to repeat it in the grid (and the pill is hidden when
            embedded since the parent provides its own header). */}
        {!embedded && deal.dealStage && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Deal Stage</p>
            <p className={valueClass}>{deal.dealStage}</p>
          </div>
        )}
        {/* Sale Price + Earnest Money show in Commission Breakdown.
            Skip in embedded mode to avoid the duplicate. */}
        {!embedded && deal.offerPrice && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Sale Price</p>
            <p className={valueClass}>{formatCurrency(deal.offerPrice)}</p>
          </div>
        )}
        {!embedded && deal.earnestMoney && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Earnest Money</p>
            <p className={valueClass}>{formatCurrency(deal.earnestMoney)}</p>
          </div>
        )}

        {/* Broker Admin Fee + Type moved to Commission Breakdown when
            embedded (see note above). */}
        {!embedded && user.role == "Admin" && deal.brokeradminfee && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Broker Admin Fee</p>
            <p className={valueClass}>
              {formatCurrency(deal.brokeradminfee)}
            </p>
          </div>
        )}
        {!embedded && user.role != "Employee" && deal.captypedescription && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Type</p>
            <p className={valueClass}>{deal.captypedescription}</p>
          </div>
        )}

        {/* Closing Date, Inspection Deadline, and Financing Deadline used
            to live here as separate grid cells. They moved into the
            Important Dates section at the bottom of this card so each
            date shows with its countdown and add-to-calendar button —
            no more duplication. */}
        {/* Inspection (Yes/No) + Financing Type moved into the Under
            Contract Milestones section in BuyerForm/SellerForm when
            embedded — they're milestone-context info. */}
        {!embedded && deal.financingType && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Financing Type</p>
            <p className={valueClass}>{deal.financingType}</p>
          </div>
        )}
        {!embedded && deal.inspection && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Inspection</p>
            <p className={valueClass}>{formatText(deal.inspection)}</p>
          </div>
        )}
        {/* CMA Information header — same h4 treatment SellerForm uses for
            its own sub-section headings ("Pre-Listing", "Marketing & Staging"),
            so the embedded summary flows visually into the form below.
            Spans both grid columns. Only shown when at least one of the
            grouped fields has a value, so we don't render a lone heading on
            data-sparse deals. */}
        {(editable ||
          deal.totalcommissionrate ||
          deal.listingDate ||
          deal.fileDeadline ||
          deal.formDeadline ||
          deal.fileFormat ||
          deal.propertyTrust ||
          deal.sellerDisclosure ||
          deal.sellHome ||
          deal.photoBook ||
          deal.preListing ||
          deal.dropOff ||
          deal.clientReferal) && (
          <button
            type="button"
            onClick={() => setCmaOpen((v) => !v)}
            aria-expanded={cmaOpen}
            className="md:col-span-2 w-full flex items-center justify-between mt-2 group"
          >
            <h4 className="text-base font-semibold text-slate-900 group-hover:text-slate-700 transition">
              {deal?.dealtype === "Buyer"
                ? "Buyer Commission Information:"
                : "CMA Information:"}
            </h4>
            <ChevronDown
              size={16}
              strokeWidth={2.5}
              className={`text-slate-400 group-hover:text-slate-600 transition-transform ${
                cmaOpen ? "" : "-rotate-90"
              }`}
            />
          </button>
        )}
        {cmaOpen && (
          <>
        {(editable || deal.totalcommissionrate) && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Commission %</p>
            {editable ? (
              <input
                type="number"
                step="0.01"
                value={editValues.totalcommissionrate ?? ""}
                onChange={(e) =>
                  onEditChange?.("totalcommissionrate", e.target.value)
                }
                placeholder="Enter Commission"
                className="font-semibold text-sm rounded-lg px-2 py-1.5 mt-1 border-2 border-gray-300 w-full outline-none focus:border-[#1E293B]"
              />
            ) : (
              <p className={valueClass}>{deal.totalcommissionrate}%</p>
            )}
          </div>
        )}
        {(editable || deal.listingDate) && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Listing Appointment Date</p>
            {editable ? (
              <input
                type="date"
                value={editValues.listingDate ?? ""}
                onChange={(e) => onEditChange?.("listingDate", e.target.value)}
                className="font-semibold text-sm rounded-lg px-2 py-1.5 mt-1 border-2 border-gray-300 w-full outline-none focus:border-[#1E293B]"
              />
            ) : (
              <p className={valueClass}>{formatDate(deal.listingDate)}</p>
            )}
          </div>
        )}
        {(editable || deal.fileDeadline) && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Need File No Later Than</p>
            {editable ? (
              <input
                type="date"
                value={editValues.fileDeadline ?? ""}
                onChange={(e) => onEditChange?.("fileDeadline", e.target.value)}
                className="font-semibold text-sm rounded-lg px-2 py-1.5 mt-1 border-2 border-gray-300 w-full outline-none focus:border-[#1E293B]"
              />
            ) : (
              <p className={valueClass}>{formatDate(deal.fileDeadline)}</p>
            )}
          </div>
        )}
        {deal.formDeadline && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Need Forms No Later Than</p>
            <p className={valueClass}>{formatDate(deal.formDeadline)}</p>
          </div>
        )}
        {deal.fileFormat && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">File Format</p>
            <p className={valueClass}>{deal.fileFormat}</p>
          </div>
        )}
        {deal.propertyTrust && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Property in a Trust</p>
            <p className={valueClass}>{formatText(deal.propertyTrust)}</p>
          </div>
        )}
        {deal.sellerDisclosure && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Sellers Disclosure</p>
            <p className={valueClass}>{formatText(deal.sellerDisclosure)}</p>
          </div>
        )}
        {deal.sellHome && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Did You Sell Them The Home</p>
            <p className={valueClass}>{formatText(deal.sellHome)}</p>
          </div>
        )}
        {deal.photoBook && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Want a Photo Book</p>
            <p className={valueClass}>{formatText(deal.photoBook)}</p>
          </div>
        )}
        {deal.preListing && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Pre-Listing Drop Off</p>
            <p className={valueClass}>{formatText(deal.preListing)}</p>
          </div>
        )}
        {deal.dropOff && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Drop Off By</p>
            <p className={valueClass}>{formatText(deal.dropOff)}</p>
          </div>
        )}
        {deal.clientReferal && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Client Referral</p>
            <p className={valueClass}>{formatText(deal.clientReferal)}</p>
          </div>
        )}
        {/* "Who Referred & Send Gift" only makes sense when there was a
            referral. Hide the tile entirely otherwise so we don't show a
            standalone "NA" cell on every non-referral deal. */}
        {deal.clientReferal === "yes" && deal.referredSendGift && (
          <div className={cellClass}>
            <p className="text-sm text-gray-500">Who Referred & Send Gift</p>
            <p className={valueClass}>{deal.referredSendGift}</p>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export default DealSummary;
